import { useState, useCallback, useEffect } from "react";
import useLoader from "./useLoader";

/**
 * Custom hook for fetching paginated list data
 * 
 * @param {Function} apiFunction - Async function that accepts params and returns data
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.initialItemsPerPage - Initial items per page (default: 10)
 * @param {Object} options.initialFilters - Initial filter values (default: {})
 * @param {string} options.initialSortBy - Initial sort field (default: null)
 * @param {string} options.initialSortOrder - Initial sort order 'asc'|'desc' (default: 'asc')
 * @param {boolean} options.autoFetch - Automatically fetch on mount (default: true)
 * 
 * @returns {Object} Hook state and methods
 * @returns {Array} data - List of items
 * @returns {boolean} loading - Loading state
 * @returns {Object} error - Error object if any
 * @returns {Object} pagination - Pagination info (currentPage, totalPages, totalItems, itemsPerPage)
 * @returns {Function} setPage - Change current page
 * @returns {Function} setItemsPerPage - Change items per page
 * @returns {Function} setFilters - Update filters
 * @returns {Function} setSortBy - Set sort field
 * @returns {Function} setSortOrder - Set sort order
 * @returns {Function} refresh - Manually refresh data
 * @returns {Function} reset - Reset to initial state
 */
const usePaginatedList = (apiFunction, options = {}) => {
  const {
    initialPage = 1,
    initialItemsPerPage = 20,
    initialFilters = {},
    initialSortBy = null,
    initialSortOrder = "asc",
    autoFetch = true,
  } = options;

  const { showLoader, hideLoader } = useLoader();

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  /**
   * Fetch data from API
   */
  const fetchData = useCallback(
    async (page = currentPage, perPage = itemsPerPage, filterObj = filters) => {
      try {
        setLoading(true);
        setError(null);
        showLoader();

        // Build parameters
        const params = {
          page: page,
          limit: perPage,
          ...filterObj,
          ...(sortBy && { sortBy }),
          ...(sortBy && { sortOrder }),
        };

        // Call API function
        const res = await apiFunction(params);
        const response = res.data;
        const pagination = response.data.pagination;
        console.log(response, "fsfd")

        if (response?.status === 200 || response?.status === 201) {
          const result = response.data;

          // Handle different response formats
          if (Array.isArray(result)) {
            setData(result);
            setTotalItems(result.length);
            setTotalPages(1);
          } else if (result?.data && Array.isArray(result.data)) {
            console.log(result, "result");
            setData(result.data);
            setTotalItems(pagination.totalItems || pagination.total || 0);
            setTotalPages(pagination.totalPages || Math.ceil((pagination.totalItems || 0) / perPage) || 1);
          } else if (result?.items && Array.isArray(result.items)) {
            // Items format
            setData(result.items);
            setTotalItems(result.totalItems || 0);
            setTotalPages(result.totalPages || Math.ceil((result.totalItems || 0) / perPage) || 1);
          } else {
            setData(Array.isArray(result) ? result : []);
            setTotalItems(0);
            setTotalPages(1);
          }

          setCurrentPage(page);
        } else {
          setError(response?.data?.message || "Failed to fetch data");
          setData([]);
        }
      } catch (err) {
        console.error("Error fetching paginated data:", err);
        setError(err?.response?.data?.message || err.message || "An error occurred");
        setData([]);
      } finally {
        setLoading(false);
        hideLoader();
      }
    },
    [apiFunction, showLoader, hideLoader, sortBy, sortOrder]
  );

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        fetchData(newPage, itemsPerPage, filters);
      }
    },
    [fetchData, itemsPerPage, filters, totalPages]
  );

  /**
   * Handle items per page change
   */
  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      setItemsPerPage(newItemsPerPage);
      fetchData(1, newItemsPerPage, filters);
    },
    [fetchData, filters]
  );

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      fetchData(1, itemsPerPage, newFilters);
    },
    [fetchData, itemsPerPage]
  );

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback(
    (field, order = "asc") => {
      setSortBy(field);
      setSortOrder(order);
      fetchData(currentPage, itemsPerPage, filters);
    },
    [fetchData, currentPage, itemsPerPage, filters]
  );

  /**
   * Manual refresh
   */
  const handleRefresh = useCallback(() => {
    fetchData(currentPage, itemsPerPage, filters);
  }, [fetchData, currentPage, itemsPerPage, filters]);

  /**
   * Reset to initial state
   */
  const handleReset = useCallback(() => {
    setCurrentPage(initialPage);
    setItemsPerPage(initialItemsPerPage);
    setFilters(initialFilters);
    setSortBy(initialSortBy);
    setSortOrder(initialSortOrder);
    setData([]);
    setTotalPages(1);
    setTotalItems(0);
    setError(null);
  }, [initialPage, initialItemsPerPage, initialFilters, initialSortBy, initialSortOrder]);

  /**
   * Auto-fetch on mount or when dependencies change
   */
  useEffect(() => {
    if (autoFetch) {
      fetchData(currentPage, itemsPerPage, filters);
    }
  }, [autoFetch]); // Only on mount if autoFetch is true

  return {
    // State
    data,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
    },
    filters,
    sortBy,
    sortOrder,

    // Methods
    setPage: handlePageChange,
    setItemsPerPage: handleItemsPerPageChange,
    setFilters: handleFilterChange,
    setSortBy: handleSortChange,
    refresh: handleRefresh,
    reset: handleReset,
    fetchData, // Direct fetch for advanced usage
  };
};

export default usePaginatedList;
